import { Button } from '@repo/ui/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@repo/ui/components/ui/dropdown-menu'
import { Input } from '@repo/ui/components/ui/input'
import { Label } from '@repo/ui/components/ui/label'
import { Textarea } from '@repo/ui/components/ui/textarea'
import { Check, ChevronDown, ExternalLink, Loader2 } from 'lucide-react'
import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { AppTopBarActionGroup, AppTopBarActions } from '@/components/app/top-bar-actions'
import { AppWorkspacePanel, AppWorkspaceTwoPane } from '@/components/app/workspace-shell'
import { normalizeOperatorBookingTheme } from '@/lib/booking-page-customization'
import { APP_CONFIG } from '@/lib/config/constants'
import { createTrpcClient } from '@/lib/config/trpc-client'
import type { BookingPageTheme } from '@/lib/operator-config/types'
import { useOperatorConfiguration } from '@/lib/operator-config/use-operator-configuration'
import { uploadFile } from '@/lib/services/upload'
import { trpc } from '@/lib/trpc'
import { CatalogPreviewBookingPage } from '@/routes/book/$operatorSlug'

const FONT_BY_SURFACE_STYLE: Record<
  BookingPageTheme['surfaceStyle'],
  BookingPageTheme['fontStyle']
> = {
  classic: 'classic',
  modern: 'modern',
  industrial: 'modern',
  editorial: 'editorial',
}

const ACCENT_OPTIONS: Array<{
  value: string
  label: string
}> = [
  { value: '#9D654F', label: 'Clay' },
  { value: '#2F6F73', label: 'Harbor' },
  { value: '#425E91', label: 'Denim' },
  { value: '#7A5741', label: 'Walnut' },
  { value: '#1F7A4D', label: 'Pine' },
]

const STYLE_OPTIONS: Array<{
  value: BookingPageTheme['surfaceStyle']
  label: string
  description: string
  accent: string
}> = [
  { value: 'modern', label: 'Modern', description: 'Clean, quiet, rounded', accent: '#2F6F73' },
  {
    value: 'industrial',
    label: 'Industrial',
    description: 'Dense, sharp, mechanical',
    accent: '#171717',
  },
  {
    value: 'editorial',
    label: 'Editorial',
    description: 'Image-led, spacious, refined',
    accent: '#7A5741',
  },
  { value: 'classic', label: 'Classic', description: 'Warm, simple, familiar', accent: '#9D654F' },
]

const SERVICE_CARD_STYLE_OPTIONS: Array<{
  value: BookingPageTheme['serviceCardStyle']
  label: string
  description: string
}> = [
  { value: 'balanced', label: 'Balanced', description: 'Comfortable cards with details' },
  { value: 'editorial', label: 'Editorial', description: 'Larger cards with more breathing room' },
  { value: 'compact', label: 'Compact', description: 'Tighter cards for faster scanning' },
]

const OVERLAY_STYLE_OPTIONS: Array<{
  value: BookingPageTheme['hero']['overlayStyle']
  label: string
  description: string
}> = [
  { value: 'soft', label: 'Soft', description: 'Keeps images visible with readable text' },
  { value: 'strong', label: 'Strong', description: 'Darkens images for maximum contrast' },
  { value: 'none', label: 'None', description: 'Minimal treatment for prepared imagery' },
]

const FLOW_STYLE_OPTIONS: Array<{
  value: BookingPageTheme['flowStyle']
  label: string
  description: string
}> = [
  { value: 'menu_first', label: 'Menu first', description: 'Service, address, time, details' },
  {
    value: 'address_first',
    label: 'Address first',
    description: 'Address, service, time, details',
  },
]

const PAGE_LAYOUT_OPTIONS: Array<{
  value: BookingPageTheme['pageLayout']
  label: string
  description: string
}> = [
  { value: 'standard', label: 'Standard', description: 'Compact booking panel only' },
  { value: 'showcase', label: 'Showcase', description: 'Hero image above booking' },
  { value: 'split', label: 'Split', description: 'Larger brand moment and wider cards' },
]

type PendingHeroImages = Partial<
  Record<'banner' | 'background', { file: File; previewUrl: string }>
>

export function AppearanceWorkspace() {
  const utils = trpc.useUtils()
  const { configuration, refreshAll } = useOperatorConfiguration()
  const catalogsQuery = trpc.services.catalogs.list.useQuery()
  const trpcClient = useMemo(() => createTrpcClient(), [])
  const operator = configuration.operator
  const activeCatalog = catalogsQuery.data?.find((catalog) => catalog.isActive) ?? null
  const bookingUrl = operator ? `${APP_CONFIG.BASE_URL}/book/${operator.slug}` : null
  const [draft, setDraft] = useState<BookingPageTheme>(() =>
    buildAppearanceDraft(operator?.bookingPageTheme)
  )
  const [pendingHeroImages, setPendingHeroImages] = useState<PendingHeroImages>({})
  const updateOperator = trpc.operator.update.useMutation({
    onSuccess: async () => {
      await Promise.all([utils.operator.get.invalidate(), refreshAll()])
      toast.success('Booking page theme saved.')
    },
    onError: (error) => toast.error('Could not save theme', { description: error.message }),
  })

  useEffect(() => {
    setDraft(buildAppearanceDraft(operator?.bookingPageTheme))
    setPendingHeroImages((current) => {
      Object.values(current).forEach((pending) => {
        URL.revokeObjectURL(pending.previewUrl)
      })
      return {}
    })
  }, [operator?.bookingPageTheme])

  const isDirty = useMemo(
    () =>
      Object.keys(pendingHeroImages).length > 0 ||
      JSON.stringify(buildThemeSavePayload(draft)) !==
        JSON.stringify(buildAppearanceDraft(operator?.bookingPageTheme)),
    [draft, operator?.bookingPageTheme, pendingHeroImages]
  )

  const saveTheme = async () => {
    let bookingPageTheme = buildThemeSavePayload(draft)

    try {
      if (pendingHeroImages.banner) {
        const bannerImageUrl = await uploadFile(pendingHeroImages.banner.file, trpcClient, {
          pathPrefix: 'booking-page',
          maxSizeBytes: 5 * 1024 * 1024,
        })
        bookingPageTheme = {
          ...bookingPageTheme,
          hero: { ...bookingPageTheme.hero, bannerImageUrl },
        }
      }

      if (pendingHeroImages.background) {
        const backgroundImageUrl = await uploadFile(pendingHeroImages.background.file, trpcClient, {
          pathPrefix: 'booking-page',
          maxSizeBytes: 5 * 1024 * 1024,
        })
        bookingPageTheme = {
          ...bookingPageTheme,
          hero: { ...bookingPageTheme.hero, backgroundImageUrl },
        }
      }

      await updateOperator.mutateAsync({ bookingPageTheme })
      setDraft(bookingPageTheme)
      setPendingHeroImages((current) => {
        Object.values(current).forEach((pending) => {
          URL.revokeObjectURL(pending.previewUrl)
        })
        return {}
      })
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save theme')
    }
  }

  if (!operator) {
    return (
      <div className="flex min-h-[320px] items-center justify-center">
        <Loader2 className="size-6 animate-spin" aria-label="Loading appearance editor" />
      </div>
    )
  }

  return (
    <AppWorkspacePanel className="relative h-full min-h-0 overflow-hidden">
      <AppTopBarActions>
        <AppTopBarActionGroup>
          {bookingUrl ? (
            <Button asChild variant="outline" size="sm">
              <a href={bookingUrl} target="_blank" rel="noreferrer">
                <ExternalLink className="size-4" />
                Open booking page
              </a>
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            disabled={updateOperator.isPending || !isDirty}
            onClick={saveTheme}
          >
            {updateOperator.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
            Save
          </Button>
        </AppTopBarActionGroup>
      </AppTopBarActions>

      <div className="flex min-h-0 flex-1 flex-col">
        <BookingPageThemeEditor
          draft={draft}
          setDraft={setDraft}
          pendingHeroImages={pendingHeroImages}
          setPendingHeroImages={setPendingHeroImages}
          catalogId={activeCatalog?.id ?? null}
          isCatalogLoading={catalogsQuery.isLoading}
        />
      </div>
    </AppWorkspacePanel>
  )
}

function BookingPageThemeEditor({
  draft,
  setDraft,
  pendingHeroImages,
  setPendingHeroImages,
  catalogId,
  isCatalogLoading,
}: {
  draft: BookingPageTheme
  setDraft: Dispatch<SetStateAction<BookingPageTheme>>
  pendingHeroImages: PendingHeroImages
  setPendingHeroImages: Dispatch<SetStateAction<PendingHeroImages>>
  catalogId: string | null
  isCatalogLoading: boolean
}) {
  const setSurfaceStyle = (surfaceStyle: BookingPageTheme['surfaceStyle']) => {
    setDraft((current) => ({
      ...current,
      surfaceStyle,
      fontStyle: FONT_BY_SURFACE_STYLE[surfaceStyle],
    }))
  }
  const selectedAccent = ACCENT_OPTIONS.find(
    (option) => option.value.toLowerCase() === draft.accentColor.toLowerCase()
  )
  const showsStorefrontHero = draft.pageLayout !== 'standard'
  function previewBookingImage(file: File | undefined, field: 'banner' | 'background') {
    if (!file) return

    const previewUrl = URL.createObjectURL(file)
    setPendingHeroImages((current) => {
      const previous = current[field]
      if (previous) URL.revokeObjectURL(previous.previewUrl)
      return { ...current, [field]: { file, previewUrl } }
    })
    setDraft((current) => ({
      ...current,
      hero: {
        ...current.hero,
        [field === 'banner' ? 'bannerImageUrl' : 'backgroundImageUrl']: previewUrl,
      },
    }))
  }

  return (
    <AppWorkspaceTwoPane
      sidebar={{
        eyebrow: 'Appearance',
        description: 'Booking page look and feel',
        contentClassName: 'space-y-7 p-4 md:p-5',
        children: (
          <>
            <div className="space-y-3">
              <OptionSelect
                label="Booking flow"
                options={FLOW_STYLE_OPTIONS}
                value={draft.flowStyle}
                onChange={(flowStyle) => setDraft((current) => ({ ...current, flowStyle }))}
              />
              <OptionSelect
                label="Page layout"
                options={PAGE_LAYOUT_OPTIONS}
                value={draft.pageLayout}
                onChange={(pageLayout) => setDraft((current) => ({ ...current, pageLayout }))}
              />
              <OptionSelect
                label="Look"
                options={STYLE_OPTIONS}
                value={draft.surfaceStyle}
                onChange={setSurfaceStyle}
              />
              <AccentSelect
                value={draft.accentColor}
                selectedLabel={selectedAccent?.label ?? 'Custom'}
                onChange={(accentColor) => setDraft((current) => ({ ...current, accentColor }))}
              />
              {showsStorefrontHero ? (
                <OptionSelect
                  label="Overlay"
                  options={OVERLAY_STYLE_OPTIONS}
                  value={draft.hero.overlayStyle}
                  onChange={(overlayStyle) =>
                    setDraft((current) => ({
                      ...current,
                      hero: {
                        ...current.hero,
                        overlayStyle,
                      },
                    }))
                  }
                />
              ) : null}
              <OptionSelect
                label="Cards"
                options={SERVICE_CARD_STYLE_OPTIONS}
                value={draft.serviceCardStyle}
                onChange={(serviceCardStyle) =>
                  setDraft((current) => ({ ...current, serviceCardStyle }))
                }
              />
              <ShowHideToggle
                label="Duration"
                value={draft.showDuration}
                onChange={(showDuration) => setDraft((current) => ({ ...current, showDuration }))}
              />
            </div>

            <div className="grid gap-2 sm:grid-cols-[7.75rem_minmax(0,1fr)] sm:items-center">
              <Label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                Menu title
              </Label>
              <Input
                value={draft.bookingCopy.serviceMenuTitle}
                maxLength={96}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    bookingCopy: {
                      ...current.bookingCopy,
                      serviceMenuTitle: event.target.value,
                    },
                  }))
                }
                placeholder="Services"
                aria-label="Menu step title"
              />
            </div>

            <div className="space-y-3 pt-2">
              <Label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                Images
              </Label>
              <HeroImageControl
                id="booking-background-image"
                label="Background"
                value={draft.hero.backgroundImageUrl}
                pending={Boolean(pendingHeroImages.background)}
                onUpload={(file) => previewBookingImage(file, 'background')}
                onRemove={() => {
                  setPendingHeroImages((current) => {
                    const previous = current.background
                    if (previous) URL.revokeObjectURL(previous.previewUrl)
                    const { background: _background, ...next } = current
                    return next
                  })
                  setDraft((current) => ({
                    ...current,
                    hero: { ...current.hero, backgroundImageUrl: '' },
                  }))
                }}
              />
              {showsStorefrontHero ? (
                <>
                  <HeroImageControl
                    id="booking-banner-image"
                    label="Banner"
                    value={draft.hero.bannerImageUrl}
                    pending={Boolean(pendingHeroImages.banner)}
                    onUpload={(file) => previewBookingImage(file, 'banner')}
                    onRemove={() => {
                      setPendingHeroImages((current) => {
                        const previous = current.banner
                        if (previous) URL.revokeObjectURL(previous.previewUrl)
                        const { banner: _banner, ...next } = current
                        return next
                      })
                      setDraft((current) => ({
                        ...current,
                        hero: { ...current.hero, bannerImageUrl: '' },
                      }))
                    }}
                  />
                  <div className="space-y-2">
                    <Label htmlFor="booking-hero-headline">Headline</Label>
                    <Input
                      id="booking-hero-headline"
                      value={draft.hero.headline}
                      maxLength={120}
                      onChange={(event) =>
                        setDraft((current) => ({
                          ...current,
                          hero: { ...current.hero, headline: event.target.value },
                        }))
                      }
                      placeholder="Book your appointment online"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="booking-hero-subheadline">Subheadline</Label>
                    <Textarea
                      id="booking-hero-subheadline"
                      value={draft.hero.subheadline}
                      maxLength={240}
                      rows={3}
                      onChange={(event) =>
                        setDraft((current) => ({
                          ...current,
                          hero: { ...current.hero, subheadline: event.target.value },
                        }))
                      }
                      placeholder="What customers can book and what happens next."
                    />
                  </div>
                </>
              ) : null}
            </div>
          </>
        ),
      }}
      detail={{
        contentClassName: 'flex flex-col p-4 md:p-5',
        children: (
          <BookingFlowPreview
            theme={draft}
            catalogId={catalogId}
            isCatalogLoading={isCatalogLoading}
          />
        ),
      }}
    />
  )
}

function ShowHideToggle({
  label,
  value,
  onChange,
}: {
  label: string
  value: boolean
  onChange: (value: boolean) => void
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-[7.75rem_minmax(0,1fr)] sm:items-center">
      <Label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </Label>
      <div className="grid h-9 grid-cols-2 rounded-md border border-input bg-background p-0.5 text-sm">
        <button
          type="button"
          className={`rounded-[4px] px-3 transition-colors ${
            value
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => onChange(true)}
        >
          Show
        </button>
        <button
          type="button"
          className={`rounded-[4px] px-3 transition-colors ${
            !value
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => onChange(false)}
        >
          Hide
        </button>
      </div>
    </div>
  )
}

function HeroImageControl({
  id,
  label,
  value,
  pending,
  onUpload,
  onRemove,
}: {
  id: string
  label: string
  value: string
  pending: boolean
  onUpload: (file: File | undefined) => void
  onRemove: () => void
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-[7.75rem_minmax(0,1fr)] sm:items-center">
      <Label
        htmlFor={id}
        className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground"
      >
        {label}
      </Label>
      <div className="flex min-w-0 items-center gap-3">
        <label
          htmlFor={id}
          className="grid h-12 w-20 shrink-0 cursor-pointer place-items-center overflow-hidden rounded-[6px] border border-border/70 bg-muted/25 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/45 hover:text-foreground"
        >
          {value ? <img src={value} alt="" className="size-full object-cover" /> : 'Choose'}
        </label>
        <div className="min-w-0 flex-1">
          {pending || value ? (
            <p className="truncate text-sm text-muted-foreground">
              {pending ? 'Ready on save' : 'Image selected'}
            </p>
          ) : null}
        </div>
        {value ? (
          <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
            Remove
          </Button>
        ) : null}
      </div>
      <input
        id={id}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={(event) => {
          onUpload(event.target.files?.[0])
          event.target.value = ''
        }}
      />
    </div>
  )
}

function OptionSelect<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string
  options: Array<{ value: T; label: string; description: string; accent?: string }>
  value: T
  onChange: (value: T) => void
}) {
  const selectedOption = options.find((option) => option.value === value) ?? options[0]

  return (
    <div className="grid gap-2 sm:grid-cols-[7.75rem_minmax(0,1fr)] sm:items-center">
      <Label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </Label>
      <select
        value={value}
        title={selectedOption.description}
        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
        onChange={(event) => onChange(event.target.value as T)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  )
}

function AccentSelect({
  value,
  selectedLabel,
  onChange,
}: {
  value: string
  selectedLabel: string
  onChange: (value: string) => void
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-[7.75rem_minmax(0,1fr)] sm:items-center">
      <Label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        Accent
      </Label>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            title="Primary buttons, highlights, and selected states"
            className="h-9 w-full justify-between rounded-md border-input bg-background px-3"
          >
            <span className="flex min-w-0 items-center gap-3">
              <span
                className="size-4 shrink-0 rounded-[3px] border border-border/70"
                style={{ backgroundColor: value }}
              />
              <span className="truncate font-medium">{selectedLabel}</span>
            </span>
            <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
          {ACCENT_OPTIONS.map((option) => (
            <DropdownMenuItem
              key={option.value}
              className="cursor-pointer gap-3"
              onClick={() => onChange(option.value)}
            >
              <span
                className="size-5 shrink-0 rounded-[4px] border border-border/70"
                style={{ backgroundColor: option.value }}
              />
              <span className="flex min-w-0 flex-1 items-center justify-between gap-3">
                <span className="truncate font-medium">{option.label}</span>
                <span className="font-mono text-xs text-muted-foreground">{option.value}</span>
              </span>
              {value.toLowerCase() === option.value.toLowerCase() ? (
                <Check className="size-4 text-primary" />
              ) : null}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

function BookingFlowPreview({
  theme,
  catalogId,
  isCatalogLoading,
}: {
  theme: BookingPageTheme
  catalogId: string | null
  isCatalogLoading: boolean
}) {
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-hidden rounded-[8px] border border-border/70 bg-background">
        <div className="h-full overflow-y-auto">
          {isCatalogLoading ? (
            <PreviewEmptyState>Loading active catalog...</PreviewEmptyState>
          ) : catalogId ? (
            <CatalogPreviewBookingPage catalogId={catalogId} themeOverride={theme} />
          ) : (
            <PreviewEmptyState>
              Create an active catalog in Services to preview the customer booking flow here.
            </PreviewEmptyState>
          )}
        </div>
      </div>
    </div>
  )
}

function PreviewEmptyState({ children }: { children: string }) {
  return (
    <div className="rounded-[8px] border border-dashed border-border/70 bg-muted/30 p-4 text-sm leading-6 text-muted-foreground">
      {children}
    </div>
  )
}

function buildAppearanceDraft(theme: BookingPageTheme | null | undefined): BookingPageTheme {
  const normalized = normalizeOperatorBookingTheme(theme)

  return {
    ...normalized,
    fontStyle: FONT_BY_SURFACE_STYLE[normalized.surfaceStyle],
  }
}

function buildThemeSavePayload(theme: BookingPageTheme): BookingPageTheme {
  return {
    ...theme,
    fontStyle: FONT_BY_SURFACE_STYLE[theme.surfaceStyle],
  }
}
